/* eslint-disable camelcase */
import LazyLoad from "/Assets/libs/vanilla-lazyload/lazyload.js";

/**
 * Image Lazy Loading class using vanilla-lazyload library
 * Lazily loads images with the "lazy-load" class when they enter the viewport.
 * Automatically detects AJAX-injected content via MutationObserver and a
 * "lazyload:update" custom event so new images are observed without manual calls.
 * 
 * Usage:
 * To manually trigger a re-scan from other code
 * Option 1: via the global instance
 * window.imageLazyLoad?.update();
 * 
 * Option 2: via custom event (no import needed)
 * document.dispatchEvent(new CustomEvent("lazyload:update"));
 */
class ImageLazyLoad {
    // ========================================
    // CONSTRUCTOR
    // ========================================

    constructor(options = {}) {
        this.options = {
            elements_selector: ".lazy-load",
            threshold: 0,
            class_loading: "lazy-loading",
            class_loaded: "lazy-loaded",
            class_error: "lazy-error",
            use_native: this.supportsNativeLazyLoading(),
            callback_loaded: this.onImageLoaded.bind(this),
            callback_error: this.onImageError.bind(this),
            ...options
        };

        this.lazyLoadInstance = null;
        this.mutationObserver = null;
        this.pendingUpdate = null;
        this.init();
    }

    // ========================================
    // INITIALIZATION METHODS
    // ========================================

    /**
     * Initialize LazyLoad instance, MutationObserver, and custom event listener
     */
    init() {
        this.lazyLoadInstance = new LazyLoad(this.options);
        this.observeDomChanges();
        this.listenForUpdateEvent();
    }

    /**
     * Watch the DOM for added nodes that contain lazy-load elements.
     * Uses a debounced update so rapid DOM mutations batch into a single call.
     */
    observeDomChanges() {
        this.mutationObserver = new MutationObserver((mutations) => {
            const hasNewLazyElements = mutations.some((mutation) =>
                Array.from(mutation.addedNodes).some((node) =>
                    node.nodeType === Node.ELEMENT_NODE &&
                    (node.matches?.(this.options.elements_selector) ||
                        node.querySelector?.(this.options.elements_selector))
                )
            );

            if (hasNewLazyElements) {
                this.debouncedUpdate();
            }
        });

        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    /**
     * Listen for a custom "lazyload:update" event so callers can explicitly
     * trigger a re-scan when needed (e.g. after programmatic DOM updates).
     */
    listenForUpdateEvent() {
        document.addEventListener("lazyload:update", () => {
            this.update();
        });
    }

    // ========================================
    // EVENT CALLBACKS
    // ========================================

    /**
     * Called when an image is successfully loaded
     * @param {HTMLElement} element - The loaded image element
     */
    onImageLoaded(element) {
        element.removeAttribute("data-srcset");
        element.removeAttribute("data-src");
    }

    /**
     * Called when an image fails to load
     * @param {HTMLElement} element - The errored image element
     */
    onImageError(element) {
        //console.warn("Image failed to load:", element);
    }

    // ========================================
    // HELPER & UTILITY METHODS
    // ========================================

    /**
     * Check if browser supports native lazy loading
     * @returns {boolean} True if native lazy loading is supported
     */
    supportsNativeLazyLoading() {
        return "loading" in HTMLImageElement.prototype;
    }

    /**
     * Debounced update — batches rapid DOM changes into a single LazyLoad update
     */
    debouncedUpdate() {
        if (this.pendingUpdate) {
            cancelAnimationFrame(this.pendingUpdate);
        }
        this.pendingUpdate = requestAnimationFrame(() => {
            this.update();
            this.pendingUpdate = null;
        });
    }

    /**
     * Update LazyLoad to check for new elements
     */
    update() {
        if (this.lazyLoadInstance) {
            this.lazyLoadInstance.update();
        }
    }

    /**
     * Load all lazy images immediately
     */
    loadAll() {
        if (this.lazyLoadInstance) {
            this.lazyLoadInstance.loadAll();
        }
    }

    /**
     * Destroy the LazyLoad instance and disconnect the MutationObserver
     */
    destroy() {
        if (this.pendingUpdate) {
            cancelAnimationFrame(this.pendingUpdate);
            this.pendingUpdate = null;
        }

        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }

        if (this.lazyLoadInstance) {
            this.lazyLoadInstance.destroy();
            this.lazyLoadInstance = null;
        }
    }
}

// ========================================
// AUTO-INITIALIZATION
// ========================================

/**
 * Initialize ImageLazyLoad only if lazy-load images exist on the page
 */
(() => {
    const initializeLazyLoad = () => {
        const lazyElements = document.querySelectorAll(".lazy-load");

        if (lazyElements.length > 0) {
            const imageLazyLoad = new ImageLazyLoad();

            // Expose instance globally for access if needed
            window.imageLazyLoad = imageLazyLoad;

            //console.log(`ImageLazyLoad initialized for ${lazyElements.length} elements. Native lazy loading: ${imageLazyLoad.options.use_native}`);
        }
    };

    // Initialize when DOM is ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initializeLazyLoad);
    } else {
        // DOM is already loaded
        initializeLazyLoad();
    }
})();

export default ImageLazyLoad;
